<template>
  <div ref="el" :class="$style.base" @click="onClick">
    <BaseRoundCheckbox
      :class="$style.checkbox"
      v-if="settings.allow_multiple_services"
      :isActive="isActive"
    />
    <div
      :class="[
        $style.info,
        { [$style.hasInfo]: data.description || settings.show_service_duration }
      ]"
    >
      <div :class="$style.left">
        <div :class="$style.name" v-test="'service-item-name'">
          {{ displayName }}
        </div>
        <div :class="$style.infoWrapper">
          <div
            :class="$style.infoItem"
            v-if="settings.show_service_duration"
            v-test="'service-item-duration'"
          >
            {{ data.duration }} {{ $t('general.minute', data.duration) }}
          </div>
          <div
            :class="$style.infoItem"
            v-if="settings.show_service_duration && data.description"
          >
            —
          </div>
          <div :class="$style.infoItem" v-test="'service-item-description'">
            {{ data.description }}
          </div>
        </div>
      </div>
      <div v-if="settings.show_service_prices" :class="$style.right">
        <div :class="[$style.price]" v-test="'service-item-price'">
          {{ currency(data.price) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import BaseRoundCheckbox from '@/components/BaseRoundCheckbox.vue';
import { currency } from '@/helpers';
import { useBookingStore } from '@/stores/booking';
import { useCompanyStore } from '@/stores/company';
import type { Service, ServiceVariation } from '@/types';
import widgetSettings from '@/widget-settings';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { inject } from 'vue';

const props = defineProps<{
  data: Service | ServiceVariation;
  isVariation?: boolean;
}>();

const emit = defineEmits(['submit', 'scrollTo']);
const gtag = inject<any>('gtag');
const { settings } = useCompanyStore();
const { serviceIds } = storeToRefs(useBookingStore());

const isActive = computed(
  () => !!serviceIds.value.find((id: number) => id === props.data.id)
);
const displayName = computed(() =>
  props.data.variation_name ? props.data.variation_name : props.data.name
);

const onClick = () => {
  if (settings.allow_multiple_services) {
    if (isActive.value) {
      serviceIds.value = serviceIds.value.filter(
        (id: number) => id !== props.data.id
      );
      gtag.trackEvent('service_deselect', {
        service_id: props.data.id
      });
    } else {
      serviceIds.value.push(props.data.id);
      gtag.trackEvent('service_select', {
        service_id: props.data.id
      });
    }
  } else {
    serviceIds.value = [props.data.id];
    emit('submit');
    gtag.trackEvent('service_select', {
      service_id: props.data.id
    });
  }
};

const el = ref();

onMounted(() => {
  if (
    widgetSettings.services.find(
      (serviceId) => Number.parseInt(serviceId) === props.data.id
    )
  ) {
    emit('scrollTo', el.value.offsetTop);
  }
});
</script>

<style lang="scss" module>
.base {
  display: flex;
  align-items: flex-start;
  padding: ($spacing * 2) $spacing;
  border-bottom: 1px solid $color-border;
  cursor: pointer;
  width: 100%;
}

.checkbox {
  margin-top: 2px;
}

.info {
  flex: 1;
  width: 100%;
  padding-left: $spacing;
  align-self: center;
  display: flex;
  justify-content: space-between;
}

.info.hasInfo {
  align-self: flex-start;

  + .checkbox {
    margin-top: -2px;
  }

  .name {
    font-weight: bold;
  }
}

.name {
  font-weight: bold;
  font-size: 16px;
  line-height: 22px;

  &::first-letter {
    text-transform: uppercase;
  }
}

.price {
  font-size: 16px;
  line-height: 22px;
}

.right {
  text-align: right;
  margin-left: $spacing;
}

.fromHeading {
  text-align: right;
}

.infoWrapper {
  margin-top: 6px;
}

.fromHeading,
.infoWrapper {
  font-size: 13px;
  line-height: 20px;
  opacity: 0.7;
}

.infoItem {
  display: inline;
}
</style>
